"use strict";

import {
    default as React,
    Component,
 } from 'react';
import PropTypes from 'prop-types';

import {
    XAxis,
    YAxis,
    XGrid,
    YGrid,
    scale,
    xDomainCount,
    yDomainCount
} from 'kaidoo-chart/core';

import CommonProps from './commonProps';

export default class ChartSvg extends Component {
    constructor(props) {
        super (props);
        this.svgContainerRef = React.createRef();
    };

    static defaultProps = {
        svgClassName: 'react-d3-core__container_svg',
        ...CommonProps
    };

    static propTypes = {
        id: PropTypes.string,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
        margins: PropTypes.object.isRequired,
        data: PropTypes.array.isRequired,
        dataSeriesConfig: PropTypes.array.isRequired,
        svgClassName: PropTypes.string.isRequired,
        xScaleSetConfig: PropTypes.array.isRequired,
        yScaleSetConfig: PropTypes.array.isRequired,
        customXScaleSetConfig: PropTypes.object,
        customYScaleSetConfig: PropTypes.object,
    };

    _mkScaleSet(type, commonProps, scaleSetConfig, custom) {
        let range;
        let domain;

        if(type === 'x') {
            range = scaleSetConfig.range ||
                [0, commonProps.width - commonProps.margins.left - commonProps.margins.right];
            domain = scaleSetConfig.domain || xDomainCount({
                    ...commonProps,
                    xRange: scaleSetConfig.range,
                    x: scaleSetConfig.valueFunc,
                }, false, true);
        }

        if(type === 'y') {
            range = scaleSetConfig.range ||
                [commonProps.height - commonProps.margins.top - commonProps.margins.bottom, 0];
            domain = scaleSetConfig.domain || yDomainCount({
                    ...commonProps,
                    yRange: scaleSetConfig.range,
                    y: scaleSetConfig.valueFunc,
                }, false, true);
        }

        return scale({
            scale: scaleSetConfig.scale,
            base: scaleSetConfig.base ? scaleSetConfig.base : null,
            range: range,
            domain: domain,
            ...custom,
        });
    };

    _execCustomStyleFuncs(scaleSetConfig, xScaleSet, yScaleSet) {
        scaleSetConfig.axis.customStyle = scaleSetConfig.axis.customStyle ?
            scaleSetConfig.axis.customStyle(xScaleSet, yScaleSet) : null;

        scaleSetConfig.axis.label.customStyle = scaleSetConfig.axis.label.customStyle ?
            scaleSetConfig.axis.label.customStyle(xScaleSet, yScaleSet) : null;

        scaleSetConfig.axis.ticks.customTickStyle = scaleSetConfig.axis.ticks.customTickStyle ?
            scaleSetConfig.axis.ticks.customTickStyle(xScaleSet, yScaleSet) : null;

        scaleSetConfig.axis.ticks.customTickLabelStyle = scaleSetConfig.axis.ticks.customTickLabelStyle ?
            scaleSetConfig.axis.ticks.customTickLabelStyle(xScaleSet, yScaleSet) : null;

        scaleSetConfig.grid.customStyle = scaleSetConfig.grid.customStyle ?
            scaleSetConfig.grid.customStyle(xScaleSet, yScaleSet) : null;

        return scaleSetConfig;
    }

    render() {


        const {
            svgClassName,
            id,
            xScaleSetConfig,
            yScaleSetConfig,
            ...propsForAllChildren
        } = this.props;

        const {
            width,
            height,
            margins
        } = this.props;

        const customX = this.props.customXScaleSetConfig;
        const customY = this.props.customYScaleSetConfig;

        let xScaleSets = xScaleSetConfig.map(
            scaleSetConfig => (this._mkScaleSet('x', propsForAllChildren, scaleSetConfig, customX))
        );

        let yScaleSets = yScaleSetConfig.map(
            scaleSetConfig => (this._mkScaleSet('y', propsForAllChildren, scaleSetConfig, customY))
        );

        let children = React.Children.map(this.props.children, (el) => {
            if(el) {
                return React.cloneElement(el, {...propsForAllChildren, xScaleSets: xScaleSets, yScaleSets: yScaleSets})
            }else {
                return null;
            }
        });

        let t = '';
        if ( margins.left !== 0 || margins.top !== 0 ) {
            t = `translate(${margins.left}, ${margins.top})`;
        }
        const chartShape = this.props.chartShape;

        return (
            <svg
                height = {height}
                width = {width}
                className = {svgClassName}
                id = {id}
                ref = {this.svgContainerRef}
            >
                <g
                    transform = {t}
                >
                    {xScaleSetConfig.map((xConfig,i) => {
                        return (
                            yScaleSetConfig.map((yConfig,j) => {
                                let xConfigCalc = this._execCustomStyleFuncs(xConfig, xScaleSets[i], yScaleSets[j]);
                                let yConfigCalc = this._execCustomStyleFuncs(yConfig, xScaleSets[i], yScaleSets[j]);
                                return (
                                    <g key={`scale-${i}-${j}`}>
                                        {chartShape({
                                            xScaleSet: xScaleSets[i],
                                            yScaleSet: yScaleSets[j],
                                            x: xConfigCalc.valueFunc,
                                            y: yConfigCalc.valueFunc,
                                            customX: customX,
                                            customY: customY,
                                            ...propsForAllChildren,
                                        })}
                                        {
                                            xConfig.grid.show ? <XGrid
                                                key={100*i+j}
                                                gridConfig={xConfigCalc.grid}
                                                axisConfig={xConfigCalc.axis}
                                                scale={xConfigCalc.scale}
                                                xScaleSet={xScaleSets[i]}
                                                yScaleSet={yScaleSets[j]}
                                                x={xConfigCalc.valueFunc}
                                                {...propsForAllChildren}
                                            /> : null
                                        }
                                        {
                                            yConfig.grid.show ? <YGrid
                                                key={1000 + 100*i+j}
                                                gridConfig={yConfigCalc.grid}
                                                axisConfig={yConfigCalc.axis}
                                                scale={yConfigCalc.scale}
                                                xScaleSet={xScaleSets[i]}
                                                yScaleSet={yScaleSets[j]}
                                                y={yConfigCalc.valueFunc}
                                                {...propsForAllChildren}
                                            /> : null
                                        }
                                        {
                                            xConfig.axis.show ? <XAxis
                                                key={2000 + 100*i+j}
                                                axisConfig={xConfigCalc.axis}
                                                scaleSet={xScaleSets[i]}
                                                x={xConfigCalc.valueFunc}
                                                {...propsForAllChildren}
                                            /> : null
                                        }
                                        {
                                            yConfig.axis.show ? <YAxis
                                                key={3000 + 100*i+j}
                                                axisConfig={yConfigCalc.axis}
                                                scaleSet={yScaleSets[j]}
                                                y={yConfigCalc.valueFunc}
                                                {...propsForAllChildren}
                                            /> : null
                                        }

                                    </g>
                                )
                            })
                        );
                    })}
                    {children}
                </g>
            </svg>
        )
    }
}
